import React, { useEffect } from "react";
import Routes from "./Routes";
import axios from "axios";
import { signOut } from "firebase/auth";
import { myAuth } from "./contexts/firebaseProvider";
import {
  KEY_TOKEN,
  KEY_USER,
  KEY_LOGOUT_REASON,
  LOGOUT_REASON_ACCOUNT_DISABLED,
} from "utils/constants";
import { trackAppOpen, trackForceLogout } from "./analytics/events";
import logger from "utils/logger";
import CookieConsent from "./components/CookieConsent";
import { API_BASE_URL } from "./config/runtime";

// Produção por padrão; emuladores só com a bandeira de `.env.e2e` no servidor
// de desenvolvimento. Ver `src/config/runtime.js`.
axios.defaults.baseURL = API_BASE_URL;

axios.interceptors.request.use(
  async (config) => {
    if (localStorage.getItem(KEY_TOKEN)) {
      config.headers.Authorization =
        "Bearer " + localStorage.getItem(KEY_TOKEN);
    }
    config.headers["api-key"] =
      "BPjzdOtLUBCm7OyFmtEoLNAp5-txD9RCCJt5iyqOHAMM8Ya9t0BKhSt5agkMq3az508fCnTaetQ3K_Rl7IHNc7U";
    return config;
  },
  (error) => Promise.reject(error),
);

const forceLogout = async (reason = "token_refresh_failed") => {
  // Torna observável o deslogamento silencioso: antes, o usuário era jogado
  // para "/" sem que nenhum evento/erro fosse registrado em lugar nenhum.
  trackForceLogout(reason);
  try {
    await signOut(myAuth);
  } catch (_) {
    // ignora erros do signOut
  }
  localStorage.removeItem(KEY_TOKEN);
  localStorage.removeItem(KEY_USER);
  // Conta desabilitada (banimento) precisa chegar como explicação na tela de
  // login. Sem isso a pessoa era jogada para fora no meio do uso e não tinha
  // como saber o que houve nem para onde recorrer.
  if (reason === LOGOUT_REASON_ACCOUNT_DISABLED) {
    try {
      sessionStorage.setItem(KEY_LOGOUT_REASON, reason);
    } catch (_) {
      // navegador sem sessionStorage (modo restrito): segue sem o aviso
    }
  }
  window.location.replace("/");
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Aguarda o Firebase terminar de restaurar a sessão persistida antes de
      // decidir. Sem isso, um 401 disparado durante o boot (token expirado no
      // localStorage, mas `currentUser` ainda não hidratado) cairia direto no
      // ramo de logout, deslogando à força um usuário com sessão válida.
      try {
        await myAuth.authStateReady();
      } catch {
        // authStateReady não deve lançar; se lançar, seguimos o fluxo abaixo.
      }

      const currentUser = myAuth.currentUser;
      if (currentUser) {
        try {
          const newToken = await currentUser.getIdToken(true);
          localStorage.setItem(KEY_TOKEN, newToken);
          originalRequest.headers.Authorization = "Bearer " + newToken;
          return axios(originalRequest);
        } catch (refreshError) {
          logger.error(
            "Falha ao renovar token após 401; deslogando:",
            refreshError,
          );
          // `auth/user-disabled` é o que o Firebase devolve para conta banida —
          // é a única pista que o app tem de que não foi um token expirado.
          await forceLogout(
            refreshError?.code === "auth/user-disabled"
              ? LOGOUT_REASON_ACCOUNT_DISABLED
              : "token_refresh_failed",
          );
        }
      } else if (
        localStorage.getItem(KEY_TOKEN) ||
        localStorage.getItem(KEY_USER)
      ) {
        // Só força logout se havia de fato uma sessão local. Um 401 vindo de
        // uma rota pública (sem token/usuário) não deve disparar reload para
        // "/", o que causaria um loop de recarga (401 → forceLogout → reload).
        await forceLogout("no_current_user");
      }
    }

    return Promise.reject(error);
  },
);

function App() {
  // Track app open on mount
  useEffect(() => {
    trackAppOpen();
  }, []);

  return (
    <>
      <Routes />
      <CookieConsent />
    </>
  );
}

export default App;
